document.addEventListener('DOMContentLoaded', () => {
  const EXPIRED_KEY = 'bundle_deal_expired';

  const section = document.querySelector('.bundle-deal');
  if (!section) return;

  const minQty = parseInt(section.dataset.bundleMinQty || '2', 10);
  const buyQty = parseInt(section.dataset.buyQty || '1', 10);
  const freeQty = parseInt(section.dataset.freeQty || '0', 10);
  const { discountCode, moneyFormat = '{{amount}}' } = section.dataset;

  const ctaBtn = section.querySelector('[data-bundle-cta]');
  const countEl = section.querySelector('[data-selected-count]');
  const barItemsEl = section.querySelector('[data-bar-items]');
  const barItemTemplate = section.querySelector('[data-bar-item-template]');
  const errorEl = section.querySelector('[data-bundle-error]');
  const totalEl = section.querySelector('[data-bar-total]');
  const compareEl = section.querySelector('[data-bar-compare]');
  const gridEl = section.querySelector('.bundle-deal__grid-section');
  const expiredEl = section.querySelector('.bundle-deal__expired');
  const overlay = section.querySelector('[data-bundle-overlay]');

  // variantId -> quantity, kept in the order products were first added
  const selection = new Map();

  const isExpired = () => {
    try {
      return localStorage.getItem(EXPIRED_KEY) === '1';
    } catch {
      return false;
    }
  };

  const showExpired = () => {
    gridEl?.classList.add('is-hidden');
    expiredEl?.classList.remove('is-hidden');
  };

  const getCard = (variantId) => section.querySelector(`.bundle-deal__card[data-variant-id="${variantId}"]`);

  const totalQty = () => [...selection.values()].reduce((sum, qty) => sum + qty, 0);

  // Theme's formatMoney is scoped inside theme.dev.js; this mirrors Liquid's money_without_trailing_zeros
  const MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;
  const formatMoney = (cents) => {
    const placeholder = moneyFormat.match(MONEY_PLACEHOLDER)?.[1] ?? 'amount';
    const isComma = placeholder.includes('comma_separator');
    const [whole, fraction] = (cents / 100).toFixed(2).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, isComma ? '.' : ',');
    const amount = fraction === '00' ? grouped : `${grouped}${isComma ? ',' : '.'}${fraction}`;
    return moneyFormat.replace(MONEY_PLACEHOLDER, amount);
  };

  // Mirrors the Shopify "buy X get Y" discount: per full group of (buyQty + freeQty), the cheapest units are free
  const calculateTotals = () => {
    const units = [];
    let compare = 0;

    for (const [variantId, qty] of selection) {
      const { price, comparePrice } = getCard(variantId).dataset;
      for (let i = 0; i < qty; i++) units.push(Number(price));
      compare += Number(comparePrice) * qty;
    }

    const freeCount = freeQty > 0 ? Math.floor(units.length / (buyQty + freeQty)) * freeQty : 0;
    const total = units
      .sort((a, b) => b - a)
      .slice(0, units.length - freeCount)
      .reduce((sum, price) => sum + price, 0);

    return { total, compare };
  };

  const renderTotal = () => {
    const { total, compare } = calculateTotals();
    totalEl.textContent = formatMoney(total);
    compareEl.textContent = formatMoney(compare);
    compareEl.classList.toggle('is-hidden', compare <= total);
  };

  const renderBar = () => {
    const items = [...selection].map(([variantId, qty]) => {
      const item = barItemTemplate.content.firstElementChild.cloneNode(true);
      const card = getCard(variantId);
      const img = item.querySelector('img');
      const qtyEl = item.querySelector('[data-bar-item-qty]');

      img.src = card?.dataset.productImage ?? '';
      img.alt = card?.dataset.productTitle ?? '';
      qtyEl.textContent = qty;
      qtyEl.classList.toggle('is-hidden', qty < 2);
      return item;
    });

    barItemsEl.replaceChildren(...items);
  };

  const renderCards = () => {
    for (const card of section.querySelectorAll('.bundle-deal__card')) {
      const qty = selection.get(card.dataset.variantId) ?? 0;
      card.classList.toggle('bundle-deal__card--selected', qty > 0);
      card.querySelector('[data-card-qty]').textContent = qty;
    }
  };

  const render = () => {
    const count = totalQty();
    renderBar();
    renderCards();
    renderTotal();
    if (countEl) countEl.textContent = count;
    if (ctaBtn) ctaBtn.disabled = count < minQty;
  };

  const changeQty = (variantId, delta) => {
    const countBefore = totalQty();
    const qty = (selection.get(variantId) ?? 0) + delta;

    if (qty > 0) {
      selection.set(variantId, qty);
    } else {
      selection.delete(variantId);
    }

    errorEl?.classList.add('is-hidden');
    render();

    if (countBefore < minQty && totalQty() >= minQty && typeof fireMilestoneConfetti === 'function') {
      fireMilestoneConfetti();
    }
  };

  const addBundleToCart = async () => {
    if (totalQty() < minQty || isExpired()) return;

    const items = [...selection].map(([id, quantity]) => ({ id: Number(id), quantity }));

    ctaBtn.disabled = true;
    ctaBtn.classList.add('is-loading');
    ctaBtn.setAttribute('aria-busy', 'true');
    errorEl?.classList.add('is-hidden');

    try {
      if (discountCode) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: discountCode }),
        });
      }

      // CartX listens for /cart/add.js and opens the drawer with the refreshed cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error(`Cart error: ${response.status}`);

      selection.clear();
    } catch (error) {
      console.error('Bundle add to cart failed:', error);
      errorEl?.classList.remove('is-hidden');
    } finally {
      ctaBtn.classList.remove('is-loading');
      ctaBtn.removeAttribute('aria-busy');
      render();
    }
  };

  const openPopup = (card) => {
    const popup = card.querySelector('[data-popup]');
    if (!popup || !overlay) return;
    popup.classList.add('is-active');
    overlay.classList.add('is-active');
    document.body.classList.add('bundle-popup-open');
  };

  const closePopup = () => {
    section.querySelector('[data-popup].is-active')?.classList.remove('is-active');
    overlay?.classList.remove('is-active');
    document.body.classList.remove('bundle-popup-open');
  };

  const toggleAccordion = (header) => {
    const isOpen = header.getAttribute('aria-expanded') === 'true';
    header.setAttribute('aria-expanded', String(!isOpen));
    header.nextElementSibling.classList.toggle('is-hidden', isOpen);
  };

  if (isExpired()) {
    showExpired();
    return;
  }

  window.addEventListener('bundle-deal-expired', showExpired);

  section.addEventListener('click', (event) => {
    const { target } = event;

    const qtyBtn = target.closest('[data-action]');
    if (qtyBtn) {
      changeQty(qtyBtn.dataset.variantId, qtyBtn.dataset.action === 'decrease' ? -1 : 1);
      return;
    }

    const popupAdd = target.closest('[data-popup-add]');
    if (popupAdd) {
      changeQty(popupAdd.dataset.variantId, 1);
      closePopup();
      return;
    }

    if (target.closest('[data-popup-close]') || target === overlay) {
      closePopup();
      return;
    }

    const accordionHeader = target.closest('.bundle-deal__popup-accordion-header');
    if (accordionHeader) {
      toggleAccordion(accordionHeader);
      return;
    }

    if (target.closest('[data-bundle-cta]')) {
      addBundleToCart();
      return;
    }

    if (target.closest('[data-popup], .bundle-deal__stepper')) return;

    const card = target.closest('.bundle-deal__card');
    if (card) openPopup(card);
  });

  render();
});
